import React, { useEffect, useMemo, useState } from 'react'
import { useMutation, useQuery } from 'react-query'
import qs from 'qs'
import { XMarkIcon } from '@heroicons/react/20/solid'

import api from 'api'
import type { Error } from 'api'
import { ListUser, User, UserRoles, UserStatus } from 'modules/UsersModule/types'
import { Hotels } from 'modules/UsersModule/components'
import { Select, Tabs, Button, Header } from 'ui-v2'
import { notification } from 'helpers'
import { Settings } from './components'
import { Employees } from '../index'

type Props = {
  id: number
  supervisor: User
  tab?: 'hotels' | 'employees' | 'user-role'
  onClose: () => void
  selectEmployee: (employeeId: number, tab?: 'hotels' | 'employees' | 'user-role') => void
}

const Employee: React.FC<Props> = ({ onClose, tab, selectEmployee, id, supervisor }) => {
  const [selectedTabKey, setSelectedTabKey] = useState('hotels')
  const [user, setUser] = useState<User | null>(null)
  const [employees, setEmployees] = useState<ListUser[]>([])

  const fetchEmployees = (key: string, userId: number): Promise<{ data: ListUser[] }> =>
    api.get(
      `/users?${qs.stringify(
        {
          length: 999,
          sort_by: 'name',
          sort_direction: 'asc',
          user_id: userId,
        },
        { skipNulls: true },
      )}`,
    )
  const { isFetching: isEmployeesLoading } = useQuery(
    ['users', supervisor.id],
    () => fetchEmployees(['users', supervisor.id].join(','), supervisor.id),
    {
      onSuccess: ({ data }) => setEmployees(data),
      onError: (error: Error) => {
        notification.error({ message: error.message })
      },
    },
  )

  const getUserById = (key: string, userId: number): Promise<{ data: User }> => api.get(`/users/${userId}`)
  const { isFetching: isUserLoading } = useQuery(['user', id], () => getUserById(['user', id].join(','), id), {
    onSuccess: ({ data }) => setUser(data),
    onError: (error: Error) => {
      notification.error({ message: error.message })
    },
  })

  const {
    mutate: updateUser,
    isLoading: isUserUpdating,
    isSuccess: isUpdatingSuccess,
  } = useMutation(
    async (data: { status: UserStatus; role?: UserRoles; user_id?: number }) => api.patch(`/users/${user?.id}`, data),
    {
      onSuccess: ({ data }) => {
        setUser(data)
        notification.success({ message: 'User has been updated' })
      },
      onError: (error: Error) => {
        notification.error({ message: error.message })
      },
    },
  )

  const managers = useMemo(
    () => employees.filter((employee) => employee.role === UserRoles.Manager && employee.id !== user?.id),
    [employees, user],
  )

  const tabs = [
    {
      key: 'hotels',
      label: 'Hotels',
    },
    ...(user?.role === UserRoles.Manager
      ? [
          {
            key: 'employees',
            label: 'Employees',
          },
        ]
      : []),
    {
      key: 'user-role',
      label: 'User role',
    },
  ]

  useEffect(() => {
    const foundTab = tabs.find((item) => item.key === tab)

    if (foundTab) {
      setSelectedTabKey(foundTab.key)
    }
  }, [tab, user]) // eslint-disable-line

  const onChangeEmployee = (value: string): void => {
    const employee = employees.find((employee) => employee.id.toString() === value)

    employee && selectEmployee(employee.id, 'hotels')
  }

  return (
    <div className="relative">
      {(isUserLoading || isEmployeesLoading) && (
        <div className="absolute top-0 left-0 right-0 bottom-0 bg-gray-50 opacity-75 z-10">
          <span className="absolute top-1/2 left-1/2 w-12 h-12 inline-flex items-center justify-center -mt-6 -ml-6">
            <svg
              aria-hidden="true"
              className="w-10 h-10 text-gray-200 animate-spin dark:text-gray-600 fill-blue-800"
              viewBox="0 0 100 101"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
                fill="currentColor"
              />
              <path
                d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
                fill="currentFill"
              />
            </svg>
          </span>
        </div>
      )}

      <Header>
        <h1 className="text-3xl !leading-7 font-medium text-gray-800 m-0">Manage Employees & Properties</h1>
        <Button
          onClick={onClose}
          variant="secondary"
          className="rounded w-[34px] p-1 inline-flex justify-center items-center"
        >
          <XMarkIcon className="w-5" />
        </Button>
      </Header>

      <div className="p-2 rounded-t bg-bg-accent-7 flex items-center justify-between gap-2 mb-2 md:px-4 md:py-3 lg:mb-0">
        <Select
          label="Selected user"
          value={employees.length > 0 ? id.toString() : undefined}
          onChange={(value) => onChangeEmployee(value || '')}
          options={employees.map((employee) => ({ value: employee.id.toString(), label: employee.name }))}
          className="w-full"
        />
      </div>

      <div className="mx-2 my-4 rounded-lg md:mx-7">
        <Tabs items={tabs} selectedKey={selectedTabKey} onChange={setSelectedTabKey} />

        {!!user && (
          <div className="relative p-2 bg-white md:p-6">
            {selectedTabKey === 'hotels' && <Hotels user={user} setUser={setUser} />}
            {selectedTabKey === 'employees' && <Employees user={user} selectEmployee={selectEmployee} />}
            {selectedTabKey === 'user-role' && (
              <Settings
                user={user}
                supervisor={supervisor}
                managers={managers}
                updateUser={updateUser}
                isUserUpdating={isUserUpdating}
                isUpdatingSuccess={isUpdatingSuccess}
              />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

export default Employee
